import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { PMField } from "../../utils/PMField";
import { useDispatch } from "react-redux";
import { mapErrors } from "../../Utils";
import {
  createProductCategoryAsync,
  updateProductCategoryAsync,
} from "../../../slices/productCategory";
import {
  CheckBadgeIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { QuillEditor } from "../../utils/quill-editor";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { fetchProductCategory } from "../../../api/productCategories";
import { ProductCategorySelector } from "../../utils/moduleSelectors/productCategorySelector";
import { PMImagePicker } from "../../utils/PMImagePicker";
import PMSwitch from "../../utils/PMSwitch";
import { PMDialog } from "../../utils/PMDialog";

export default function CategoryDialog(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    category: {},
  });

  const { open, onClose, selected } = props;
  const { category, isLoading } = itemState;
  const [readOnly, setReadOnly] = useState(props.readOnly);

  const getCategory = async () => {
    if (!selected?.id) {
      setItemState((prevState) => ({
        ...prevState,
        isLoading: false,
        category: {},
      }));
      return;
    }

    try {
      const res = await fetchProductCategory(selected.id);
      if (res.data?.id) {
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          category: res.data,
        }));
      }
    } catch (err) {
      let errors = null;
      if (err?.response?.data?.errors) {
        errors = mapErrors(err.response.data.errors);
      } else {
        errors = ["Something went wrong"];
      }
      setItemState((prevState) => ({
        ...prevState,
        isLoading: false,
        category: {},
        error: true,
        errors: errors,
      }));
    }
  };

  useEffect(() => {
    if (open) {
      setItemState((prevState) => ({
        ...prevState,
        isLoading: true,
      }));
      if (selected?.id) {
        getCategory();
      } else {
        setTimeout(() => {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            category: {},
          }));
        }, 100);
      }
    } else {
      setItemState((prevState) => ({
        ...prevState,
        category: {},
      }));
    }
  }, [open, selected?.id]);

  const createOrUpdateDispatch = async (data) => {
    if (category?.id) {
      const res = await dispatch(updateProductCategoryAsync(data));
      return res;
    } else {
      const res = await dispatch(createProductCategoryAsync(data));
      return res;
    }
  };

  const getDialogTitle = () => {
    if (readOnly) {
      return t("Category");
    }
    return category?.id ? t("Edit Category") : t("New Category");
  };

  const headerActions = [
    {
      title: "toggle-readonly",
      label: readOnly ? t("Edit") : t("Read Only"),
      icon: readOnly ? PencilIcon : XMarkIcon,
      action: () => setReadOnly(!readOnly),
      hidden: false,
    },
  ];

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={getDialogTitle()}
      size="4xl"
      isLoading={isLoading}
      headerActions={headerActions}
      closeOnOutsideClick={true}
    >
      {!isLoading && (
        <Formik
          enableReinitialize
          initialValues={{
            id: category?.id,
            title: category?.title || "",
            description: category?.description || "",
            parent_category: category?.parent?.id || category?.parent_category || null,
            active: category?.active !== undefined ? category.active : true,
            public: category?.public !== undefined ? category.public : true,
            image_id: category?.image?.id || category?.image_id || null,
          }}
          validationSchema={Yup.object({
            title: Yup.string()
              .required(t("Title is required"))
              .min(1, t("Title must be at least 1 character")),
          })}
          onSubmit={async (values, helpers) => {
            try {
              const categoryData = {
                id: category?.id || values.id,
                title: values.title,
                description: values.description || "",
                parent_category: values.parent_category || 0,
                image_id: values.image_id || null,
              };

              const result = await createOrUpdateDispatch(categoryData);
              if (result?.payload?.id || result?.payload) {
                onClose(false);
                toast.success(
                  category?.id ? t("Category updated") : t("Category created")
                );
                return;
              } else {
                throw new Error("Server error");
              }
            } catch (err) {
              console.error(err);
              toast.error(
                t("Something went wrong. Could not save data!")
              );
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: err.message });
            }
          }}
        >
          {(formik) => (
              <Form className="space-y-5">
                {/* Title Field */}
                <PMField
                  name="title"
                  type="text"
                  label={t("Title")}
                  readOnly={readOnly}
                  {...formik}
                />

                {/* Description Field */}
                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    {t("Description")}
                  </label>
                  <QuillEditor
                    name="description"
                    rows={4}
                    readOnly={readOnly}
                    {...formik}
                  />
                </div>

                {/* Parent Category Field */}
                <ProductCategorySelector
                  name="parent_category"
                  label={t("Parent Category")}
                  selected={
                    category?.parent?.id
                      ? {
                          id: category.parent.id,
                          title:
                            category.parent.title ||
                            category.parent.displayTitle ||
                            "",
                        }
                      : category?.parent_category
                      ? { id: category.parent_category, title: "" }
                      : null
                  }
                  disabled={readOnly}
                  {...formik}
                />

                {/* Settings and Image Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Settings */}
                  <div className="space-y-3">
                    <PMSwitch
                      name="active"
                      label={t("Active")}
                      disabled={readOnly}
                      {...formik}
                    />
                    <PMSwitch
                      name="public"
                      label={t("Public")}
                      disabled={readOnly}
                      {...formik}
                    />
                  </div>

                  {/* Image */}
                  <div>
                    <PMImagePicker
                      label={t("Image")}
                      selected={category?.image}
                      name="image_id"
                      disabled={readOnly}
                      module={null}
                      module_param={null}
                      {...formik}
                    />
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="flex justify-end gap-2 pt-4 mt-6 border-t border-gray-100">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
                    onClick={onClose}
                  >
                    {t("Close")}
                  </button>

                  {!readOnly && (
                    <button
                      type="submit"
                      disabled={formik.isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {category?.id ? t("Update") : t("Create")}
                    </button>
                  )}
                </div>
              </Form>
          )}
        </Formik>
      )}
    </PMDialog>
  );
}

CategoryDialog.defaultProps = {
  selected: null,
  readOnly: false,
  open: false,
  onClose: () => toast.error("Missing onClose function. Cannot close dialog"),
};
